import { useEffect, useState } from 'react';
import dynamic from 'next/dynamic';
import { LngLatBounds } from 'mapbox-gl';
import useMap from '../../hooks/useMap';
import { useSelector } from '../../redux';
import HoveredResultCardAndPolyline from '../HoveredResultCardAndPolyline';

const AttachedRecordingsOverlay = dynamic(() => import('./AttachedRecordingsOverlay'), { ssr: false });
const HeatmapOverlay = dynamic(() => import('./HeatmapOverlay'), { ssr: false });
const AllMapsOverlay = dynamic(() => import('./AllMapsOverlay'), { ssr: false });
const PendingTrailsOverlay = dynamic(() => import('./PendingTrailsOverlay'), { ssr: false });
const AllRecordingsOverlay = dynamic(() => import('./AllRecordingsOverlay'), { ssr: false });
const PopularRecordingsOverlay = dynamic(() => import('./PopularRecordingsOverlay'), { ssr: false });
const NearbyTrailsOverlay = dynamic(() => import('./NearbyTrailsOverlay'), { ssr: false });
const GeoBoundariesOverlay = dynamic(() => import('./GeoBoundariesOverlay'), { ssr: false });

type DynamicStyleOverlaysProps = {
  excludedTrailIds?: number[];
  excludedGeoBoundaryIds?: number[];
};

const daysAgoDate = (days: number) => {
  const nowInMs = new Date().getTime();
  const daysAgoInMs = days * (24 * 60 * 60 * 1000);
  return new Date(nowInMs - daysAgoInMs);
};

/**
 * Renders overlays for enabled dynamic map styles. Dynamic styles are ones that require some sort of work
 * (API request, Algolia search, etc) outside of just a layer from a static url.
 * Some dynamic styles are very specific to a trail map so they aren't included in this more general component.
 * Any other dynamic styles not listed here need implementation from the monolith.
 */
const DynamicStyleOverlays = ({ excludedTrailIds, excludedGeoBoundaryIds }: DynamicStyleOverlaysProps) => {
  const { dynamicStyleIds, heatmapStyleIds, supportedDynamicStyleIds, trailId } = useSelector(state => ({
    dynamicStyleIds: state.map.dynamicStyleIds,
    heatmapStyleIds: state.map.heatmapStyleIds,
    supportedDynamicStyleIds: state.map.supportedDynamicStyleIds,
    trailId: state.map.trailId
  }));
  const map = useMap();
  const [bounds, setBounds] = useState<LngLatBounds | undefined | null>(map?.getBounds());

  useEffect(() => {
    map?.on('moveend', e => {
      setBounds(e.target.getBounds());
    });
  }, [map]);

  return (
    <>
      {supportedDynamicStyleIds.map(id => {
        const isEnabled = dynamicStyleIds.includes(id);
        if (!isEnabled) {
          return null;
        }
        const maxResultCount = dynamicStyleIds.includes('limit') ? 50 : undefined;
        const minimumDate = dynamicStyleIds.includes('after') ? daysAgoDate(60) : undefined;
        const showHeatmap = heatmapStyleIds.includes(id);
        switch (id) {
          case 'heatmap': {
            if (trailId) {
              return <AttachedRecordingsOverlay trailId={trailId} />;
            }
            return bounds ? <HeatmapOverlay key={id} bounds={bounds} maxResultCount={maxResultCount} minimumDate={minimumDate} /> : null;
          }
          case 'nearbyTrails':
            return bounds ? <NearbyTrailsOverlay key={id} bounds={bounds} showHeatmap={showHeatmap} excludedTrailIds={excludedTrailIds} /> : null;
          case 'mapsAll':
            return bounds ? (
              <AllMapsOverlay key={id} bounds={bounds} maxResultCount={maxResultCount} minimumDate={minimumDate} showHeatmap={showHeatmap} />
            ) : null;
          case 'pendingTrails':
            return bounds ? (
              <PendingTrailsOverlay key={id} bounds={bounds} maxResultCount={maxResultCount} minimumDate={minimumDate} showHeatmap={showHeatmap} />
            ) : null;
          case 'recordingsAll':
            return bounds ? (
              <AllRecordingsOverlay key={id} bounds={bounds} maxResultCount={maxResultCount} minimumDate={minimumDate} showHeatmap={showHeatmap} />
            ) : null;
          case 'recordingsPop':
            return bounds ? (
              <PopularRecordingsOverlay
                key={id}
                bounds={bounds}
                maxResultCount={maxResultCount}
                minimumDate={minimumDate}
                showHeatmap={showHeatmap}
              />
            ) : null;
          case 'geoBoundaries':
            return bounds ? <GeoBoundariesOverlay key={id} bounds={bounds} excludedIds={excludedGeoBoundaryIds} /> : null;
          default:
            return null;
        }
      })}
      <HoveredResultCardAndPolyline />
    </>
  );
};

export default DynamicStyleOverlays;
